import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  jsonb,
  index,
  date,
  varchar,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { clients, employees, dependents } from "./business";
import { plans } from "./plans";
import { enrollments } from "./enrollments";

// Immutable premium charge ledger (Plan Library v2 —
// docs/PLAN_RATING_ACCEPTANCE_SPEC.md). The system RECORDS premiums for
// reporting/deductions/commissions; it does not bill or collect.
//
// Rules enforced here + by DB trigger (migration 0094) + chargeService:
// - A posted row is never UPDATEd or DELETEd (scenario 09). All corrections are
//   new rows: a 'reversal' referencing the charge it offsets (equal/opposite
//   amount) and, when re-rated, a fresh 'charge' (scenarios 05/20/21).
// - Every charge carries its full resolution snapshot so it is self-describing
//   without dereferencing current link/plan state (scenario 12).
// - serviceMonth (what month the premium covers) is distinct from postedAt
//   (when the row was entered), so retroactive postings are distinguishable
//   (scenario 04).
export const premiumCharges = pgTable(
  "premium_charges",
  {
    id: serial("id").primaryKey(),
    clientId: integer("client_id").notNull().references(() => clients.id, { onDelete: "restrict" }),
    employeeId: integer("employee_id").notNull().references(() => employees.id, { onDelete: "restrict" }),
    dependentId: integer("dependent_id").references(() => dependents.id, { onDelete: "restrict" }),
    enrollmentId: integer("enrollment_id").notNull().references(() => enrollments.id, { onDelete: "restrict" }),
    planId: integer("plan_id").notNull().references(() => plans.id, { onDelete: "restrict" }),
    clientPlanId: integer("client_plan_id"), // implicit FK -> client_plans.id; null = legacy/linkless
    serviceMonth: date("service_month").notNull(), // always first of month
    postedAt: timestamp("posted_at").notNull().defaultNow(), // entry date
    entryType: text("entry_type").notNull(), // 'charge' | 'reversal'
    reversesChargeId: integer("reverses_charge_id"), // implicit self-FK; set on reversals
    // monthly | retro_enrollment | retro_termination | rate_correction |
    // plan_change | tier_change
    reason: text("reason").notNull(),
    amount: decimal("amount", { precision: 10, scale: 2 }).notNull(), // negative on reversals
    // --- resolution snapshot (immutable, self-describing — scenario 12) ---
    planYearStart: date("plan_year_start").notNull(),
    ratingAreaId: integer("rating_area_id"),
    rateVersionId: integer("rate_version_id"), // implicit FK -> plan_rate_versions.id; null = legacy versionless rates
    rateVersionKey: text("rate_version_key"), // e.g. "2026-01-01 r2"; "" = legacy null-start version
    rateStructureType: text("rate_structure_type"),
    ageBand: text("age_band"),
    tobaccoApplied: boolean("tobacco_applied"),
    coverageTier: text("coverage_tier"),
    ageDeterminationDate: date("age_determination_date"),
    // extras: ageDeterminationRule, midYearTrigger, planCode, ratingBasis, ...
    snapshot: jsonb("snapshot").$type<Record<string, unknown>>(),
    createdById: varchar("created_by_id"), // users.id (varchar UUID)
  },
  (t) => ({
    clientMonthIdx: index("premium_charges_client_month_idx").on(t.clientId, t.serviceMonth),
    enrollmentIdx: index("premium_charges_enrollment_idx").on(t.enrollmentId),
    planMonthIdx: index("premium_charges_plan_month_idx").on(t.planId, t.serviceMonth),
    employeeIdx: index("premium_charges_employee_idx").on(t.employeeId),
    reversesIdx: index("premium_charges_reverses_idx").on(t.reversesChargeId),
  })
);

export const insertPremiumChargeSchema = createInsertSchema(premiumCharges).omit({
  id: true,
  postedAt: true,
});
export type PremiumCharge = typeof premiumCharges.$inferSelect;
export type InsertPremiumCharge = z.infer<typeof insertPremiumChargeSchema>;
